<script setup>
import { ref, computed } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import { momentMixin } from '@tools/moment';
import { trans, transchoice } from '@elements/translation';
import Button from '@atoms/components/Button.vue';
import { useAdafStore } from '@stores/useAdafStore';

const adafStore = useAdafStore();
const dealPayment = adafStore.selectedDealPayment;
const { deal } = dealPayment;

const localAmount = ref(dealPayment.price);
const amountError = ref(null);
const localEndDate = ref(deal.end_date?.iso);
const localIsUnlimited = ref(!localEndDate.value);

const emit = defineEmits([ 'close' ]);

const validateAmount = () => {
  if (localAmount.value === null || localAmount.value === undefined || localAmount.value < 0) {
    amountError.value = trans('gestion.adaf.slider.field.price.error_empty');
    return;
  }

  amountError.value = null;
};

const onEndDateChange = (date) => {
  if (date) {
    localEndDate.value = date;
  }
};

const onUnlimitedChange = () => {
  if (localIsUnlimited.value) {
    localEndDate.value = null;
  }
};

const isValidateDisabled = computed(() => amountError.value || (!localEndDate.value && !localIsUnlimited.value));

const onValidate = async () => {
  validateAmount();

  if (isValidateDisabled.value) {
    return;
  }

  const isSubscriptionEdited = await adafStore.editSubscription(localAmount.value, localEndDate.value, localIsUnlimited.value);

  if (isSubscriptionEdited) {
    emit('close');
  }
};
</script>

<template>
  <div class="slider-adaf">
    <div class="slider-adaf__content">
      <div class="slider-adaf__summary">
        <span class="slider-adaf__summary--title">
          {{ deal.buyer_orga_name }} - {{ adafStore.selectedDealPayment.price }}
        </span>

        <span>{{ deal.buyer.full_name }}</span>

        <div class="slider-adaf__summary--name">
          {{ adafStore.displaySpaceLabel(deal.first_office_offer.address) }}
          <br>{{ adafStore.displayOfficesLabel(deal.office_offers_collection) }}
        </div>
      </div>

      <div class="slider-adaf__form">
        <div>
          <label for="adaf-amount-price">{{ transchoice('gestion.adaf.slider.subscription.field.price', { date: momentMixin.methods.getSimpleDateFrFirstDayNextMonth() }) }}</label>

          <div class="input-icon">
            <input
              id="adaf-amount-price"
              v-model="localAmount"
              :class="{ 'error': amountError }"
              type="number"
              min="0"
              @blur="validateAmount"
              @input="validateAmount"
            >
            <i>
              {{ trans('global.price_tax') }}&nbsp;{{ trans('global.currency_symbol') }}
            </i>
          </div>

          <span v-show="amountError" class="error-message">{{ amountError }}</span>
        </div>

        <div class="slider-adaf__form--start-date">
          <label>{{ trans('gestion.adaf.slider.subscription.start_date') }}</label>

          <VueDatePicker
            name="start_date"
            locale="fr"
            :enable-time-picker="false"
            auto-apply
            disabled
            format="dd/MM/yyyy"
            :model-value="momentMixin.methods.getSimpleDateFR(deal.start_date.iso)"
          >
            <template #input-icon>
              <span class="datepicker__icon datepicker__icon--datepicker" />
            </template>
          </VueDatePicker>
        </div>

        <div class="slider-adaf__form--end-date">
          <label>{{ trans('gestion.adaf.slider.subscription.end_date') }}</label>

          <VueDatePicker
            v-model="localEndDate"
            name="end_date"
            locale="fr"
            :enable-time-picker="false"
            auto-apply
            :disabled="localIsUnlimited"
            format="dd/MM/yyyy"
            @update:modelValue="onEndDateChange"
          >
            <template #input-icon>
              <span class="datepicker__icon datepicker__icon--datepicker" />
            </template>
          </VueDatePicker>

          <div class="slider-adaf__form--checkbox">
            <input
              id="unlimited-checkbox"
              v-model="localIsUnlimited"
              type="checkbox"
              @change="onUnlimitedChange"
            >
            <label for="unlimited-checkbox">{{ trans('gestion.adaf.slider.subscription.unlimited') }}</label>
          </div>
        </div>
      </div>
    </div>

    <div class="slider-adaf__footer">
      <div class="slider-adaf__footer-buttons">
        <Button variant="secondary" @click="emit('close')">
          {{ trans('gestion.adaf.slider.ctas.cancel') }}
        </Button>

        <Button
          variant="primary"
          :class="{ 'loading-btn': adafStore.ctaLoading }"
          :disabled="isValidateDisabled || adafStore.ctaLoading"
          @click="onValidate"
        >
          {{ trans('gestion.adaf.slider.ctas.create') }}
        </Button>
      </div>
    </div>
  </div>
</template>
